import { FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AddressSearch } from '@/components/AddressSearch';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { sitesApi, type Site } from '@/api/endpoints';
import { Alert, Button, Card, Field, Input, Select, SettingsSection } from '@/components/ui';
import { GeofenceMap } from '@/components/GeofenceMap';
import { chipClass } from '@/features/filters/FilterChip';
import { getCurrentFix, LocationError } from '@/native';
import { TIME_ZONES, timeZoneLabel } from '@/lib/timezones';
import { useErrorMessage } from '@/hooks/useErrorMessage';

/**
 * 打卡半径的常用档位。
 *
 * 这些数字不是拍脑袋来的：GPS 在楼宇密集处误差常达 20–50m，
 * 所以最小档也要 80m —— 再小工人会站在工地里却打不上卡。
 * 上限放到 1000m 是给大型场地用的。
 */
const RADIUS_PRESETS = [
  { value: 80, labelKey: 'site.radiusSmall' },
  { value: 150, labelKey: 'site.radiusMedium' },
  { value: 300, labelKey: 'site.radiusLarge' },
  { value: 1000, labelKey: 'site.radiusHuge' },
];

const MIN_RADIUS = 50;
const MAX_RADIUS = 5000;

/** 最长班次（CR-052）：8 小时以下正常一天班就会被判漏打；24 小时以上跨到第二个班了 */
const MIN_SHIFT_HOURS = 8;
const MAX_SHIFT_HOURS = 24;

const formOf = (site: Site) => ({
  name: site.name,
  nameEn: site.nameEn ?? '',
  address: site.address ?? '',
  lat: String(site.lat),
  lng: String(site.lng),
  geofenceRadiusM: String(site.geofenceRadiusM),
  maxShiftHours: String(site.maxShiftMinutes / 60),
  timezone: site.timezone,
  defaultRate: site.defaultRateCents == null ? '' : String(site.defaultRateCents / 100),
});

/**
 * 工地设置的表单（CR-082：左边说明、右边表单的设置页，后台常见做法）。
 *
 * 改之前是先看一张只读卡片、点「编辑工地设置」换成表单、存完再换回去；现在打开就是表单，
 * 改了哪一项底下的「保存更改」才亮，保存栏贴在屏幕底（三段加地图有两屏长）。
 * `readOnly`：没有 `site.manage` 的人（路由本身就挡了，这里兜底）整张表灰掉。
 */
export function SiteSettingsForm({ site, readOnly }: { site: Site; readOnly: boolean }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const toMessage = useErrorMessage();

  const [form, setForm] = useState(() => formOf(site));
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [locating, setLocating] = useState(false);

  const initial = formOf(site);
  const dirty = (Object.keys(initial) as Array<keyof typeof initial>).some((key) => form[key] !== initial[key]);

  const lat = Number(form.lat);
  const lng = Number(form.lng);
  const radius = Number(form.geofenceRadiusM);
  const coordsValid = form.lat.trim() !== '' && form.lng.trim() !== '' && Number.isFinite(lat) && Number.isFinite(lng);
  const radiusValid = Number.isFinite(radius) && radius >= MIN_RADIUS && radius <= MAX_RADIUS;
  const maxShiftHours = Number(form.maxShiftHours);
  const maxShiftValid =
    form.maxShiftHours.trim() !== '' &&
    Number.isFinite(maxShiftHours) &&
    maxShiftHours >= MIN_SHIFT_HOURS &&
    maxShiftHours <= MAX_SHIFT_HOURS;

  const mutation = useMutation({
    mutationFn: () =>
      sitesApi.update(site.id, {
        name: form.name.trim(),
        nameEn: form.nameEn.trim() || null,
        address: form.address.trim() || null,
        lat,
        lng,
        geofenceRadiusM: radius,
        maxShiftMinutes: Math.round(maxShiftHours * 60),
        timezone: form.timezone,
        // 清空时发 null 而不是 undefined，否则字段会被忽略、清不掉
        defaultRateCents: form.defaultRate ? Math.round(Number(form.defaultRate) * 100) : null,
        // 归档不在这个表单里：它是页面最下面单独的按钮 + 二次确认（CR-072）
      }),
    onSuccess: (next) => {
      // 服务端存的值可能和输入的写法不一样（「25.0」→ 25），以存下来的为准，保存按钮才会灰回去
      setForm(formOf(next));
      setSaved(true);
      void queryClient.invalidateQueries({ queryKey: ['site', site.id] });
      void queryClient.invalidateQueries({ queryKey: ['sites'] });
    },
    onError: (err) => setError(toMessage(err)),
  });

  /** 管理员人就站在工地上时，直接取当前位置当中心点最准 */
  async function useCurrentLocation() {
    setError(null);
    setLocating(true);
    try {
      const fix = await getCurrentFix();
      setForm((f) => ({ ...f, lat: fix.lat.toFixed(6), lng: fix.lng.toFixed(6) }));
      setSaved(false);
    } catch (err) {
      setError(
        err instanceof LocationError && err.kind === 'DENIED'
          ? t('errors.PUNCH_LOCATION_REQUIRED')
          : t('site.locateFailed'),
      );
    } finally {
      setLocating(false);
    }
  }

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    if (!coordsValid) {
      setError(t('site.coordsInvalid'));
      return;
    }
    if (!radiusValid) {
      setError(t('site.radiusInvalid', { min: MIN_RADIUS, max: MAX_RADIUS }));
      return;
    }
    if (!maxShiftValid) {
      setError(t('site.maxShiftInvalid', { min: MIN_SHIFT_HOURS, max: MAX_SHIFT_HOURS }));
      return;
    }
    mutation.mutate();
  }

  const patch = (next: Partial<typeof form>) => {
    setForm((f) => ({ ...f, ...next }));
    setSaved(false);
  };
  const set = (key: keyof typeof form) => (e: { target: { value: string } }) => patch({ [key]: e.target.value });

  return (
    // clip 而不是 hidden：hidden 会让卡片自己成为滚动容器，底下的保存栏就贴不住屏幕底
    <Card className="overflow-clip">
      <form onSubmit={handleSubmit}>
        <fieldset disabled={readOnly} className="contents">
          <SettingsSection title={t('site.basicTitle')} description={t('site.basicHint')}>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label={t('site.name')} htmlFor="s-name" required>
                <Input id="s-name" value={form.name} onChange={set('name')} maxLength={120} required />
              </Field>
              <Field label={t('site.nameEn')} htmlFor="s-name-en">
                <Input id="s-name-en" value={form.nameEn} onChange={set('nameEn')} maxLength={120} />
              </Field>
              <div className="sm:col-span-2">
                <Field label={t('site.address')} htmlFor="s-address">
                  <AddressSearch
                    id="s-address"
                    value={form.address}
                    onChange={(address) => patch({ address })}
                    onPick={(result) =>
                      patch({ address: result.address, lat: result.lat.toFixed(6), lng: result.lng.toFixed(6) })
                    }
                  />
                </Field>
              </div>
            </div>
          </SettingsSection>

          <SettingsSection title={t('site.geofence')} description={t('site.geofenceSectionHint')}>
            <div className="flex flex-col gap-4">
              {/* 把围栏画出来：光看「150 米」这个数字，看不出它实际罩住多大范围 */}
              {coordsValid && radiusValid && (
                <GeofenceMap
                  lat={lat}
                  lng={lng}
                  radiusM={radius}
                  heightClass="h-64 max-md:h-52"
                  onMove={readOnly ? undefined : (nextLat, nextLng) => patch({ lat: nextLat.toFixed(6), lng: nextLng.toFixed(6) })}
                />
              )}

              <Field label={t('site.geofenceRadius')} htmlFor="s-radius" hint={t('site.geofenceHint')} required>
                <div className="flex flex-wrap items-center gap-2">
                  {RADIUS_PRESETS.map((preset) => (
                    <button
                      key={preset.value}
                      type="button"
                      onClick={() => patch({ geofenceRadiusM: String(preset.value) })}
                      aria-pressed={radius === preset.value}
                      className={chipClass(radius === preset.value)}
                    >
                      <span className="tabular-nums font-semibold text-ink">{preset.value} m</span>
                      <span className="text-ink-3">{t(preset.labelKey)}</span>
                    </button>
                  ))}
                  <span className="flex items-center gap-1.5">
                    <Input
                      id="s-radius"
                      type="number"
                      value={form.geofenceRadiusM}
                      onChange={set('geofenceRadiusM')}
                      min={MIN_RADIUS}
                      max={MAX_RADIUS}
                      step={10}
                      inputMode="numeric"
                      className="w-24 tabular-nums"
                      required
                    />
                    <span className="text-body text-ink-3">{t('site.meters')}</span>
                  </span>
                </div>
              </Field>
              {radiusValid && radius < 100 && <Alert tone="info">{t('site.radiusTooTightWarning')}</Alert>}

              <div className="grid items-end gap-4 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto]">
                <Field label={t('site.latitude')} htmlFor="s-lat" required>
                  <Input id="s-lat" value={form.lat} onChange={set('lat')} inputMode="decimal" className="tabular-nums" required />
                </Field>
                <Field label={t('site.longitude')} htmlFor="s-lng" required>
                  <Input id="s-lng" value={form.lng} onChange={set('lng')} inputMode="decimal" className="tabular-nums" required />
                </Field>
                <Button type="button" variant="secondary" onClick={() => void useCurrentLocation()} loading={locating}>
                  {t('site.useCurrentLocation')}
                </Button>
              </div>
            </div>
          </SettingsSection>

          <SettingsSection title={t('site.attendanceTitle')} description={t('site.maxShiftHint')}>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label={t('site.maxShift')} htmlFor="s-max-shift">
                <Input
                  id="s-max-shift"
                  type="number"
                  min={MIN_SHIFT_HOURS}
                  max={MAX_SHIFT_HOURS}
                  step={0.5}
                  inputMode="decimal"
                  value={form.maxShiftHours}
                  onChange={set('maxShiftHours')}
                  className="tabular-nums"
                />
              </Field>
              <Field label={`${t('site.defaultRate')} (${site.currency})`} htmlFor="s-rate">
                <Input
                  id="s-rate"
                  value={form.defaultRate}
                  onChange={set('defaultRate')}
                  inputMode="decimal"
                  placeholder="25.00"
                  className="tabular-nums"
                />
              </Field>
              <div className="sm:col-span-2">
                <Field label={t('site.timezone')} htmlFor="s-tz" hint={t('site.timezoneHint')}>
                  <Select id="s-tz" value={form.timezone} onChange={set('timezone')}>
                    {TIME_ZONES.map((tz) => (
                      <option key={tz.value} value={tz.value}>
                        {timeZoneLabel(tz)}
                      </option>
                    ))}
                  </Select>
                </Field>
              </div>
            </div>
          </SettingsSection>
        </fieldset>

        {!readOnly && (
          // 贴在屏幕底：改了哪一项都不用滚回顶上找保存
          <div className="sticky-bar -mt-px flex flex-wrap items-center justify-end gap-2 border-t border-rule bg-surface px-5 py-3 max-md:px-4">
            {error && (
              <div className="mr-auto min-w-0 flex-1 basis-60">
                <Alert>{error}</Alert>
              </div>
            )}
            {!error && saved && !dirty && (
              <p role="status" className="mr-auto text-caption text-ok">
                {t('site.settingsSaved')}
              </p>
            )}
            <Button
              type="button"
              variant="secondary"
              disabled={!dirty}
              onClick={() => {
                setForm(formOf(site));
                setError(null);
              }}
            >
              {t('app.revert')}
            </Button>
            <Button type="submit" loading={mutation.isPending} disabled={!dirty}>
              {t('site.saveChanges')}
            </Button>
          </div>
        )}
      </form>
    </Card>
  );
}
